import { MultiplayerRuntime } from "../MultiplayerRuntime";
import LocalGameManager from "../managers/LocalGameManager";
import RemoteGameManager from "../managers/RemoteGameManager";
import {
  GameRuntimeController,
  GameRuntimeCallbacks,
} from "../controller/GameRuntimeController";
import { GameController } from "../../controller/GameController";
import { IInputSource } from "../../managers/input-source";
import KeyboardInputManager, {
  loadConfig,
} from "../../managers/keyboard-input-manager";
import TouchInputManager from "../../managers/touch-input-manager";
import { GameState } from "../../../core/protocol/types";
import { Packet } from "../../../core/protocol/packets";
import { AnnouncerPackType } from "../../../core/engine/packs/announcers/AnnouncerPackFactory";

export interface RuntimeInputInitOptions {
  document?: Document;
  enableTouch?: boolean;
}

export interface RuntimeInputBridge {
  setSources(sources: IInputSource[]): void;
  addSource(source: IInputSource): void;
  clearSources(): void;
  getSources(): IInputSource[];
  initializeDefaultSources(options?: RuntimeInputInitOptions): void;
  dispose(): void;
}

export interface RuntimePacketBridge {
  setQueuePacketCallback(callback: (packet: Packet) => void): void;
  getPendingPackets(): any[];
  getPendingAttacks(): any[];
}

export interface RuntimeStateBridge {
  subscribe(listener: (state: GameState | null) => void): () => void;
  getSnapshot(): GameState | null;
}

export interface RuntimeAnnouncerBridge {
  setFragPack(type: AnnouncerPackType): void;
  // Future: setLinesPack(type: AnnouncerPackType): void;
  // Future: setTauntPack(type: AnnouncerPackType): void;
}

export interface GameLoopViewBindings {
  getCanvasElement: () => HTMLCanvasElement | null;
  getCanvasSize: () => number;
  inputSources?: IInputSource[];
  callbacks?: GameRuntimeCallbacks;
}

export interface MultiplayerRuntimeHandle {
  readonly runtime: MultiplayerRuntime;
  readonly controller: GameController;
  readonly managers: {
    local: LocalGameManager;
    remote: RemoteGameManager;
  };
  readonly input: RuntimeInputBridge;
  readonly packets: RuntimePacketBridge;
  readonly state: RuntimeStateBridge;
  readonly announcer: RuntimeAnnouncerBridge;
  /**
   * Start the visual game loop once the React surface is mounted.
   */
  startGameLoop(bindings: GameLoopViewBindings): void;
  /**
   * Stop the visual game loop (called when leaving the gameplay surface).
   */
  stopGameLoop(): void;
  /**
   * Resize the underlying GameView.
   */
  resizeGameView(): void;
  /**
   * Dispose runtime resources (controller, managers, subscriptions).
   */
  dispose(): void;
  /**
   * Subscribe to state updates (forwarded from MultiplayerRuntime).
   */
  subscribe(listener: (state: GameState | null) => void): () => void;
  /**
   * Get the latest cached state snapshot.
   */
  getState(): GameState | null;
}

class MultiplayerRuntimeHandleImpl implements MultiplayerRuntimeHandle {
  readonly runtime: MultiplayerRuntime;
  readonly controller: GameController;
  readonly managers: { local: LocalGameManager; remote: RemoteGameManager };
  readonly input: RuntimeInputBridge;
  readonly packets: RuntimePacketBridge;
  readonly state: RuntimeStateBridge;
  readonly announcer: RuntimeAnnouncerBridge;

  private runtimeController: GameRuntimeController | null = null;
  private runtimeSubscription?: () => void;

  constructor(runtime: MultiplayerRuntime) {
    this.runtime = runtime;
    this.controller = runtime.getController();
    this.managers = {
      local: new LocalGameManager(),
      remote: new RemoteGameManager(),
    };
    this.input = new RuntimeInputBridgeImpl();
    this.packets = new RuntimePacketBridgeImpl(this.controller);
    this.state = new RuntimeStateBridgeImpl(this.runtime);
    this.announcer = new RuntimeAnnouncerBridgeImpl(this);

    // Ensure GameController always routes remote packets through this manager,
    // even before a view exists. This mirrors the original Net_list ownership.
    this.controller.setRemoteGameManager(this.managers.remote);

    // Allow local game manager to register queue handlers through the packet bridge.
    this.managers.local.setQueuePacketRegistrar((callback) => {
      this.packets.setQueuePacketCallback(callback);
    });

    // Initialize default input sources when running in a browser.
    this.input.initializeDefaultSources();
  }

  startGameLoop(bindings: GameLoopViewBindings): void {
    if (this.runtimeController) {
      return;
    }

    this.runtimeController = new GameRuntimeController(
      {
        gameController: this.controller,
        localGameManager: this.managers.local,
        remoteGameManager: this.managers.remote,
        inputSources:
          bindings.inputSources && bindings.inputSources.length > 0
            ? bindings.inputSources
            : this.input.getSources(),
        getCanvasElement: bindings.getCanvasElement,
        getCanvasSize: bindings.getCanvasSize,
      },
      bindings.callbacks
    );

    this.runtimeSubscription = this.runtime.subscribe((state) => {
      this.runtimeController?.handleGameState(state);
    });
  }

  stopGameLoop(): void {
    if (this.runtimeSubscription) {
      this.runtimeSubscription();
      this.runtimeSubscription = undefined;
    }

    if (this.runtimeController) {
      this.runtimeController.dispose();
      this.runtimeController = null;
    }
  }

  resizeGameView(): void {
    this.runtimeController?.resizeView();
  }

  notifyFragPackChange(type: AnnouncerPackType): void {
    this.runtimeController?.setFragAnnouncerPack(type);
  }

  dispose(): void {
    this.stopGameLoop();
    this.managers.local.cleanup();
    this.managers.remote.cleanup();
    this.input.dispose();
    this.runtime.dispose();
  }

  subscribe(listener: (state: GameState | null) => void): () => void {
    return this.runtime.subscribe(listener);
  }

  getState(): GameState | null {
    return this.runtime.getState();
  }
}

class RuntimeInputBridgeImpl implements RuntimeInputBridge {
  private sources: IInputSource[] = [];
  private keyboardManager?: KeyboardInputManager;
  private touchManager?: TouchInputManager;

  setSources(sources: IInputSource[]): void {
    this.sources = [...sources];
  }

  addSource(source: IInputSource): void {
    this.sources = [...this.sources, source];
  }

  clearSources(): void {
    this.sources = [];
  }

  getSources(): IInputSource[] {
    return this.sources;
  }

  initializeDefaultSources(options: RuntimeInputInitOptions = {}): void {
    const targetDocument =
      options.document ||
      (typeof document !== "undefined" ? document : undefined);
    const enableTouch = options.enableTouch ?? true;

    const nextSources: IInputSource[] = [];

    if (targetDocument) {
      if (!this.keyboardManager) {
        this.keyboardManager = new KeyboardInputManager(
          targetDocument,
          loadConfig(),
          true
        );
      }
      nextSources.push(this.keyboardManager);
    }

    if (enableTouch) {
      if (!this.touchManager) {
        this.touchManager = new TouchInputManager();
      }
      nextSources.push(this.touchManager);
    }

    if (nextSources.length > 0) {
      this.setSources(nextSources);
    }
  }

  dispose(): void {
    this.clearSources();
    if (this.keyboardManager) {
      this.keyboardManager.destroy();
      this.keyboardManager = undefined;
    }
    this.touchManager = undefined;
  }
}

class RuntimePacketBridgeImpl implements RuntimePacketBridge {
  private queueDelegate?: (packet: Packet) => void;
  private pendingPackets: Packet[] = [];

  constructor(private readonly controller: GameController) {
    // Use the explicitly exposed method on GameController
    this.controller.setQueuePacketCallback((packet: Packet) => {
        if (this.queueDelegate) {
            this.queueDelegate(packet);
        } else {
            this.pendingPackets.push(packet);
        }
    });
  }

  setQueuePacketCallback(callback: (packet: Packet) => void): void {
    this.queueDelegate = callback;
    if (this.pendingPackets.length > 0) {
      const buffered = [...this.pendingPackets];
      this.pendingPackets = [];
      buffered.forEach((packet) => this.queueDelegate?.(packet));
    }
  }

  getPendingPackets(): any[] {
    if (this.pendingPackets.length > 0) {
      return [...this.pendingPackets];
    }
    // GameController facade might not have getPendingPackets, return empty or check
    return (this.controller as any).getPendingPackets ? (this.controller as any).getPendingPackets() : [];
  }

  getPendingAttacks(): any[] {
    return (this.controller as any).getPendingAttacks ? (this.controller as any).getPendingAttacks() : [];
  }
}

class RuntimeStateBridgeImpl implements RuntimeStateBridge {
  constructor(private readonly runtime: MultiplayerRuntime) {}

  subscribe(listener: (state: GameState | null) => void): () => void {
    return this.runtime.subscribe(listener);
  }

  getSnapshot(): GameState | null {
    return this.runtime.getState();
  }
}

class RuntimeAnnouncerBridgeImpl implements RuntimeAnnouncerBridge {
  constructor(private readonly handle: MultiplayerRuntimeHandleImpl) {}

  setFragPack(type: AnnouncerPackType): void {
    // Update localStorage
    localStorage.setItem("fragAnnouncerPack", JSON.stringify(type));
    
    this.handle.notifyFragPackChange(type);
  }
}

export function createMultiplayerRuntimeHandle(
  runtime: MultiplayerRuntime
): MultiplayerRuntimeHandle {
  return new MultiplayerRuntimeHandleImpl(runtime);
}


